import { Button } from 'src/components/atoms';
import { border } from 'src/components/foundations/tokens';
import { textStyleCss } from 'src/theme/atlas';
import { paginationFooterFixedUiClearance } from 'src/theme/paginationFooterFixedUiClearance';
import styled from 'styled-components';

export const CategoryDetailsPageContainer = styled.div`
  background-color: ${(props) =>
    props.theme.atlas.semantic.color.surface.variant};
  padding: 24px;
  width: 100%;
  box-sizing: border-box;
  flex: 1;
  min-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

export const StyledCreateFormButton = styled(Button)`
  border-radius: 6px;
`;

export const CategoryDetailsEmptyStateWrapper = styled.div`
  & button {
    border-radius: 6px;
  }
`;

export const CategoryDetailsContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  margin-left: 0;
  margin-right: auto;
  box-sizing: border-box;
`;

export const CategoryDetailsHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  flex-wrap: wrap;
`;

export const CategoryDetailsBackAndTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

export const CategoryDetailsTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

export const CategoryDetailsBackButtonWrapper = styled.div`
  flex-shrink: 0;
`;

export const CategoryDetailsTitle = styled.h1`
  margin: 0;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.headingH3)}
  word-break: break-word;
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
`;

export const CategoryDetailsDescription = styled.p`
  margin: 0;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  color: ${(props) => props.theme.atlas.semantic.color.type.muted};
`;

export const CategoryDetailsActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

export const CategoryDetailsFormsSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const CategoryDetailsFormsSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export const CategoryDetailsFormsSectionTitle = styled.h3`
  margin: 0;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.body)}
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
`;

export const CategoryDetailsTableWrapper = styled.div`
  margin-top: 0;
  border-radius: ${border.radius.lg}px;
  overflow: hidden;
  ${(props) => props.theme.boxShadows.shadowLarge}
  background-color: ${(props) =>
    props.theme.atlas.semantic.color.surface.default};
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  tbody tr {
    cursor: pointer;
  }
`;

export const CategoryDetailsLoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 320px;
  width: 100%;
`;

export const CategoryDetailsEmptyWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

export const CategoryDetailsErrorWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

export const CategoryDetailsActionButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  pointer-events: auto;
`;

export const CategoryDetailsLanguageTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

export const CategoryDetailsFooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  margin-top: 16px;
  ${paginationFooterFixedUiClearance}
`;

export const CategoryDetailsFooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  flex-wrap: nowrap;
`;

export const CategoryDetailsPaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

export const CategoryDetailsShowingText = styled.span`
  color: ${(props) => props.theme.atlas.semantic.color.type.muted};
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)}
`;

export const CategoryDetailsEditFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
  width: 100%;
`;

export const CategoryDetailsEditTitleInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 16px;
  border-radius: 3px;
  border: 1px solid
    ${(props) => props.theme.atlas.semantic.color.outline.default};
  background-color: ${(props) =>
    props.theme.atlas.semantic.color.surface.default};
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.headingH3)}
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  outline: none;

  &::placeholder {
    color: ${(props) => props.theme.atlas.semantic.color.type.muted};
  }

  &:focus {
    border-color: ${(props) => props.theme.atlas.semantic.color.ui.focusRing};
  }
`;

export const CategoryDetailsEditDescriptionInput = styled.textarea`
  width: 100%;
  min-height: 64px;
  box-sizing: border-box;
  padding: 8px 16px;
  border-radius: 3px;
  border: 1px solid
    ${(props) => props.theme.atlas.semantic.color.outline.default};
  background-color: ${(props) =>
    props.theme.atlas.semantic.color.surface.default};
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  outline: none;
  resize: vertical;

  &::placeholder {
    color: ${(props) => props.theme.atlas.semantic.color.type.muted};
  }

  &:focus {
    border-color: ${(props) => props.theme.atlas.semantic.color.ui.focusRing};
  }
`;

export const CategoryDetailsEditFieldError = styled.span`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.caption)}
  color: ${(props) => props.theme.atlas.semantic.color.status.error.text};
`;
